import { useEffect, useMemo, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import TimezoneSelect, {
  allTimezones,
  useTimezoneSelect,
} from "react-timezone-select";
import { type Translationtype } from "utils";
import { resolveTimeZone } from "../helpers/timeZone";

interface IFormTimezoneProps {
  setValue?: any;
  register: any;
  errors: any;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  type?: string;
  name: string;
  title: string;
  tooltip?: string;
  t: Translationtype;
  control: any;
  initialPlaceHolder?: string;
  offsetControl: any;
  abbrev?: string;
  isRequired?: boolean;
  locationTimezone?: string;
}
export interface OptionValue {
  label: string;
  value: string;
  id: string;
}
export function FormTimezone({
  errors,
  className,
  name,
  title,
  t,
  control,
  setValue,
  abbrev,
  offsetControl,
  isRequired = false,
  locationTimezone,
}: IFormTimezoneProps) {
  const labelStyle = "original";
  const timezones = {
    ...allTimezones,
  };
  const { parseTimezone } = useTimezoneSelect({
    labelStyle,
    timezones,
  });

  const browserTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    []
  );
  const defaultTimezone = abbrev
    ? undefined
    : locationTimezone || browserTimezone;
  const [initTimezone, setInitTimezone] = useState(defaultTimezone);
  const hasResolvedAbbrev = useRef(false);

  // Resolve the stored value (IANA id or legacy abbreviation) inside useEffect (not during render)
  useEffect(() => {
    if (!abbrev || hasResolvedAbbrev.current) return;
    hasResolvedAbbrev.current = true;

    const resolved = resolveTimeZone(abbrev, Object.keys(allTimezones));
    // The picker has no "UTC" entry; its "GMT" entry is the same zone.
    const timeZoneResult =
      resolved === "UTC" ? "GMT" : resolved ?? browserTimezone;

    if (setValue) {
      setValue(name, timeZoneResult);
    }
    setInitTimezone(timeZoneResult);
  }, [abbrev, setValue, name, browserTimezone]);

  // Update timezone when event location changes
  useEffect(() => {
    if (locationTimezone && !abbrev) {
      setInitTimezone(locationTimezone);
      if (setValue) {
        setValue(name, locationTimezone, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
      try {
        const parsed = parseTimezone(locationTimezone);
        offsetControl(parsed.abbrev);
      } catch {
        offsetControl(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationTimezone]);

  // Report offset when initTimezone is determined
  useEffect(() => {
    if (initTimezone) {
      try {
        offsetControl(parseTimezone(initTimezone).abbrev);
      } catch {
        offsetControl(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initTimezone]);

  return (
    <div className={`${className || ""} w-full`}>
      <label htmlFor={name} className="w-full block mb-1 text-textColor">
        {title}
        {isRequired ? <span className="text-red-500"> *</span> : null}
      </label>
      <Controller
        name={name}
        control={control}
        defaultValue={initTimezone}
        render={({ field }) => (
          <TimezoneSelect
            {...field}
            unstyled
            value={field.value || initTimezone}
            classNames={{
              control: ({ isDisabled, isFocused }) =>
                `input w-full min-h-[48px] px-4 ${
                  isDisabled ? "!bg-gray-200" : ""
                } ${isFocused ? "!border-2 !border-borderColor/40" : ""}`,
              valueContainer: () => "p-0",
              input: () => "m-0 p-0",
              indicatorsContainer: () => "h-[48px]",
              menu: () =>
                "z-[9999] mt-1 rounded-2xl border border-gray-200 bg-white shadow-md",
              option: ({ isFocused, isSelected }) =>
                `px-4 py-2 cursor-pointer ${
                  isSelected ? "bg-gray-100" : ""
                } ${isFocused ? "bg-gray-50" : ""}`,
            }}
            onChange={(selected: any) => {
              offsetControl(selected.abbrev);
              field.onChange(selected.value);
            }}
          />
        )}
      />

      {errors[name]?.message ? (
        <p className="text-sm my-3 text-error">
          {t(`${errors[name]?.message}` as any)}
        </p>
      ) : null}
    </div>
  );
}
